// Tool and training types for the frontend
export * from './tools'
export * from './training'
export * from './cmi5'

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  page: number
  per_page: number
  total: number
  total_pages: number
}

// User types
// The RBAC tier taxonomy, lowest to highest. Values are the exact role names
// the server sends on the wire (the `users.role` enum was retired in #77);
// mirrored from `server/src/models.rs`'s `role` module. `guest` is the
// logged-in baseline; `historical` is a lapsed member; `active` holds
// member-level access.
export enum UserRole {
  Guest = 'guest',
  Historical = 'historical',
  Active = 'active',
  Staff = 'staff',
  Admin = 'admin',
}

export interface User {
  id: string
  username: string
  email: string
  full_name: string
  is_active: boolean
  role: UserRole
  created_at: string
  updated_at: string
  /** Set when the user has at least one confirmed MFA method. */
  mfa_enrolled_at?: string | null
  /** RBAC role names the user holds (from /auth/me). */
  roles?: string[]
  /** Effective RBAC permission keys (from /auth/me). */
  permissions?: string[]
  /**
   * Whether the address has been confirmed.
   *
   * Optional because a server older than the email work does not send it, and
   * because `undefined` and `false` must not be treated alike: the first means
   * "this deployment does not track confirmation", the second means "not
   * confirmed".
   */
  email_verified?: boolean
  profile: Record<string, unknown>
  meta: Record<string, unknown>
}

/**
 * One of a user's email addresses (#118). `email` on `User` is the row with
 * `is_primary`; the others also sign in but receive no account mail.
 */
export interface UserEmail {
  id: string
  user_id: string
  email: string
  is_primary: boolean
  /** When the owner confirmed it; null while unconfirmed. */
  verified_at: string | null
  created_at: string
}

/** Lifecycle state of a member access card (server `card_status` enum). */
export enum CardStatus {
  /** Opens the member's tools/doors. */
  Active = 'active',
  /** Deliberately turned off (lost / compromised / on hold); still bound to the member. */
  Disabled = 'disabled',
  /** Returned to the pool; the code may be reissued to someone else. */
  Released = 'released',
}

/** A member access card (RFID / fob / sticker / phone tag). */
export interface Card {
  id: string
  user_id: string
  /**
   * A masked hint of the code -- an ellipsis and the last few characters (#108).
   * The full plaintext is sealed at rest and decrypted only on the server; it
   * never reaches the client. Enough to tell a member's cards apart, and it is
   * NOT what you send to issue a card (that request carries the real code).
   */
  code_hint: string
  status: CardStatus
  last_used_at?: string | null
  issued_at: string
  disabled_at?: string | null
  released_at?: string | null
  disabled_reason?: string | null
  created_at: string
  updated_at: string
}

// Profile types
export enum ProfileFieldType {
  Text = 'Text',
  /** Ordered list of free-form strings, edited as chips. */
  TextArray = 'TextArray',
  Email = 'Email',
  Phone = 'Phone',
  Number = 'Number',
  Date = 'Date',
  Boolean = 'Boolean',
  Select = 'Select',
}

export interface ProfileFieldSelectOptions {
  options: string[]
}

export interface ProfileField {
  key: string
  label: string
  field_type: ProfileFieldType | { Select: ProfileFieldSelectOptions }
  required: boolean
  help_text?: string
}

export interface UserConfig {
  profile_fields: ProfileField[]
  profiles_enabled: boolean
}

export interface ProfileResponse {
  user_id: string
  profile: Record<string, unknown>
}

export interface UpdateProfileRequest {
  profile: Record<string, unknown>
}

export interface ProfileConfigResponse {
  profile_fields: ProfileField[]
  profiles_enabled: boolean
}

export interface UpdateProfileConfigRequest {
  profile_fields: ProfileField[]
  profiles_enabled: boolean
}

// Auth types
export interface LoginRequest {
  username_or_email: string
  password: string
}

export interface LoginResponse {
  token: string
  user: User
  expires_in: number
  /** Set by the server when MFA enforcement requires this user to enroll. */
  must_enroll_mfa?: boolean
}

/** Returned by `/auth/login` when the user has MFA enrolled. */
export interface MfaChallenge {
  mfa_required: true
  challenge_token: string
  methods: Array<'totp' | 'webauthn' | 'recovery'>
  /** A serialized PublicKeyCredentialRequestOptionsJSON for navigator.credentials.get(). */
  webauthn_options: unknown | null
}

export type LoginOutcome = LoginResponse | MfaChallenge

export function isMfaChallenge(x: unknown): x is MfaChallenge {
  return !!x && typeof x === 'object' && (x as Record<string, unknown>).mfa_required === true
}

// ===== MFA =====

export interface MfaStatus {
  enabled: boolean
  /** Any confirmed authenticator app. The list is the per-app view (#118). */
  totp_enrolled: boolean
  totp_authenticators: MfaTotpAuthenticator[]
  webauthn_count: number
  recovery_codes_remaining: number
  must_enroll: boolean
}

/** A user merge (#118): what moving `absorbed` into `survivor` would do. */
export interface MergeWarning {
  code: string
  detail: string
}
export interface MergeParty {
  id: string
  username: string
  email: string
  full_name: string
}
export interface MergePlan {
  survivor: MergeParty
  absorbed: MergeParty
  /** Rows that would be re-pointed, keyed `table.column`. */
  moves: Record<string, number>
  warnings: MergeWarning[]
}
/** One Stripe customer a user is known by (#150). */
export interface StripeCustomerLink {
  id: string
  customer_id: string
  subscription_id: string | null
  subscription_status: string | null
  created_at: string
  updated_at: string
  /** The one checkout and the Billing Portal would use. */
  current: boolean
}

/** The administrator's view of a user's second factors (#151). Never a secret. */
export interface AdminTotpFactor {
  id: string
  label: string
  created_at: string
  confirmed_at: string | null
}
export interface AdminWebauthnFactor {
  id: string
  label: string
  created_at: string
  last_used_at: string | null
}
export interface AdminMfaView {
  totp: AdminTotpFactor[]
  webauthn: AdminWebauthnFactor[]
  recovery_codes_remaining: number
  mfa_enrolled_at: string | null
}

/** An alert (#87): an audit event classified notice or above. */
export interface AlertAcknowledgement {
  audit_log_id: string
  user_id: string | null
  note: string | null
  acknowledged_at: string
}
export interface Alert {
  id: string
  event_type: string
  category: string
  severity: 'info' | 'notice' | 'warning' | 'critical'
  user_id: string | null
  actor_id: string | null
  event_data: unknown
  created_at: string
  acknowledgement: AlertAcknowledgement | null
}
export interface AlertSummary {
  unacknowledged: Record<string, number>
  unacknowledged_total: number
}
export interface AlertClassification {
  categories: string[]
  severities: string[]
  events: Record<string, { category: string; severity: string }>
}
export interface Paginated<T> {
  items: T[]
  page: number
  per_page: number
  total: number
  total_pages: number
}

/** A pair of accounts that may be one person (#38/#118), with why. */
export interface DuplicateCandidate {
  /** The older account. */
  a: MergeParty
  b: MergeParty
  /** `same_name`, `email_alias`, `shared_card`. */
  reasons: string[]
}
export interface MergeOutcome {
  merge_id: string
  survivor_id: string
  absorbed_id: string
  moved: Record<string, number>
  warnings: MergeWarning[]
}

/** One enrolled authenticator app. Never carries the secret. */
export interface MfaTotpAuthenticator {
  id: string
  label: string
  created_at: string
  confirmed_at: string | null
}

export interface MfaTotpSetup {
  /** The pending setup; passed back on confirm. */
  id: string
  label: string
  secret_base32: string
  otpauth_uri: string
}

export interface MfaRecoveryCodes {
  recovery_codes: string[]
}

export interface MfaWebauthnCredential {
  id: string
  label: string
  created_at: string
  last_used_at: string | null
}

export interface MfaWebauthnRegisterBegin {
  challenge_token: string
  /** Server-formatted PublicKeyCredentialCreationOptionsJSON. */
  options: unknown
}

// ===== Doors =====

export type DoorRuleKind = 'role' | 'user' | 'card' | 'open_access'
export type DoorRuleEffect = 'allow' | 'deny'
export type DoorAccessMethod = 'rfid' | 'qr_checkin' | 'admin_remote'

export interface Door {
  id: string
  name: string
  location: string | null
  description: string | null
  /**
   * #182: the pulse the door's latch is released for, in ms -- read off the
   * door's `strike` binding, or the site default when it has none. Was the
   * door's own `unlock_duration_ms`.
   */
  pulse_ms: number
  /** Where `pulse_ms` came from. */
  pulse_source: 'strike' | 'default'
  enabled: boolean
  created_at: string
  updated_at: string
  place_id_from?: string | null
  place_id_to?: string | null
}

export interface DoorAccessRule {
  id: string
  /** #101: rules are now keyed on the resource (a door or a tool). */
  resource_id: string
  kind: string
  value: string
  effect: string
  created_at: string
  /** Optional reusable schedule. `null` = the rule applies 24/7. */
  schedule_id?: string | null
}

export interface DoorDetail extends Door {
  rules: DoorAccessRule[]
}

export interface DoorAccessEvent {
  id: string
  door_id: string
  user_id: string | null
  method: string
  card_id_attempted: string | null
  granted: boolean
  reason: string | null
  ip_address: string | null
  occurred_at: string
  created_at: string
}

export interface CreateDoorRequest {
  name: string
  location?: string | null
  description?: string | null
  enabled?: boolean
  /** Required. Use a special place (e.g. `Outside`) for exterior doors. */
  place_id_from: string
  /** Required. */
  place_id_to: string
}

export interface UpdateDoorRequest {
  name?: string
  /** Pass `null` to clear; omit to leave unchanged. */
  location?: string | null
  description?: string | null
  enabled?: boolean
  /** PATCH-style: set to a real place ID (special places included).
      Doors can no longer be set to `null` — model exterior with a
      special place like `Outside`. */
  place_id_from?: string
  place_id_to?: string
}

export interface AddDoorRuleRequest {
  kind: DoorRuleKind
  value: string
  effect?: DoorRuleEffect
  /** Optional reusable schedule. Omit or pass `null` for "always". */
  schedule_id?: string | null
}

export interface DoorInfo {
  id: string
  name: string
  location: string | null
  enabled: boolean
  you_are_authorized: boolean
  reason: string | null
}

export interface DoorCheckinResult {
  unlocked: boolean
  reason: string | null
}

// ===== Places (configurable hierarchy) =====

export interface Place {
  id: string
  parent_id: string | null
  place_type: string
  name: string
  description: string | null
  external_id: string | null
  created_at: string
  updated_at: string
  /** Marked by operator as a "special" place (Outside, Common Area, Parking,
      …). Special places ignore the configured type-ordering rules and must
      be roots. */
  is_special: boolean
}

export interface PlaceConfig {
  enabled: boolean
  /** Ordered list, top → leaf. Index = depth. */
  types: string[]
}

export interface PlaceAttachedCounts {
  doors: number
  tools: number
  devices: number
}

export interface PlaceDetail extends Place {
  /** Top-down: `[root, ..., immediate_parent]`. Does not include this place. */
  ancestors: Place[]
  children: Place[]
  attached: PlaceAttachedCounts
}

// ===== Power topology (#42): circuits -> outlets -> receptacles =====

export interface PowerConfig {
  enabled: boolean
}

export interface PowerCircuit {
  id: string
  breaker_label: string
  voltage_rating: number
  /** Amperage limit. Decimals travel as strings. */
  amperage_limit: string
  /** Upstream/trunk circuit, or null for a top-level circuit. */
  parent_circuit_id: string | null
  created_at: string
  updated_at: string
  /** Emergency lockout (#44). `lockout_source` is server_aggregate | edge_fast_trip. */
  locked_out: boolean
  lockout_reason: string | null
  lockout_source: string | null
  locked_out_at: string | null
  locked_out_by: string | null
}

export interface PowerCircuitDetail extends PowerCircuit {
  /** Top-down: `[trunk, ..., immediate_parent]`. Excludes this circuit. */
  ancestors: PowerCircuit[]
  outlets: PowerOutlet[]
}

export interface PowerOutlet {
  id: string
  circuit_id: string
  place_id: string
  label: string
  /** Free-text position within the room. */
  location: string | null
  created_at: string
  updated_at: string
}

export interface PowerOutletDetail extends PowerOutlet {
  receptacles: PowerReceptacle[]
}

export interface PowerReceptacle {
  id: string
  outlet_id: string
  label: string
  created_at: string
  updated_at: string
}

export interface CreateCircuitRequest {
  breaker_label: string
  voltage_rating: number
  amperage_limit: string
  parent_circuit_id?: string | null
}

export interface UpdateCircuitRequest {
  breaker_label?: string
  voltage_rating?: number
  amperage_limit?: string
  /** Send null to promote to a top-level trunk; omit to leave unchanged. */
  parent_circuit_id?: string | null
}

export interface CreateOutletRequest {
  circuit_id: string
  place_id: string
  label: string
  location?: string | null
}

export interface UpdateOutletRequest {
  circuit_id?: string
  place_id?: string
  label?: string
  location?: string | null
}

export interface CreateReceptacleRequest {
  outlet_id: string
  label: string
}

export interface UpdateReceptacleRequest {
  outlet_id?: string
  label?: string
}

// Live telemetry (#43). Decimals travel as strings.
export interface ToolPowerState {
  tool_id: string
  last_draw_amps: string | null
  last_voltage: string | null
  reported_max_voltage: string | null
  reported_amperage_limit: string | null
  last_reported_at: string
  created_at: string
  updated_at: string
  /** Tool-scoped emergency lockout from a firmware self-trip (#44). */
  locked_out: boolean
  lockout_reason: string | null
  locked_out_at: string | null
}

export interface CircuitDraw {
  circuit_id: string
  total_draw_amps: string
}

export interface PowerTelemetry {
  circuits: CircuitDraw[]
  tools: ToolPowerState[]
}

// ===== Home links (admin-curated, audience-gated) =====

export type HomeLinkAudience = 'everyone' | 'anonymous' | 'logged_in' | 'member' | 'staff'

export interface HomeLink {
  id: string
  label: string
  url: string
  description: string | null
  icon: string | null
  audience: HomeLinkAudience
  sort_order: number
  enabled: boolean
  created_at: string
  updated_at: string
  /** RFC-3339; when set and in the past, the public endpoint hides this link. */
  expires_at?: string | null
}

export interface CreateHomeLinkRequest {
  label: string
  url: string
  description?: string | null
  icon?: string | null
  audience: HomeLinkAudience
  sort_order?: number
  enabled?: boolean
  /** RFC-3339 (`new Date(...).toISOString()`); pass `null` for no expiry. */
  expires_at?: string | null
}

export interface UpdateHomeLinkRequest {
  label?: string
  url?: string
  description?: string | null
  icon?: string | null
  audience?: HomeLinkAudience
  sort_order?: number
  enabled?: boolean
  /** Pass `null` to clear an existing expiry; omit to leave unchanged. */
  expires_at?: string | null
}

// ===== Schedules (weekly windows attached to access rules) =====

export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export interface ScheduleInterval {
  day: DayOfWeek
  /** HH:MM 24-hour */
  start: string
  /** HH:MM 24-hour; must be strictly greater than `start`. */
  end: string
}

export interface Schedule {
  id: string
  name: string
  description: string | null
  intervals: ScheduleInterval[]
  created_at: string
  updated_at: string
  is_public: boolean
}

export interface CreateScheduleRequest {
  name: string
  description?: string | null
  intervals: ScheduleInterval[]
  is_public?: boolean
}

export interface UpdateScheduleRequest {
  name?: string
  description?: string | null
  intervals?: ScheduleInterval[]
  is_public?: boolean
}

export interface CreatePlaceRequest {
  name: string
  place_type: string
  parent_id?: string | null
  description?: string | null
  external_id?: string | null
  is_special?: boolean
}

export interface UpdatePlaceRequest {
  name?: string
  place_type?: string
  /** Pass `null` to promote to a root; omit to leave unchanged. */
  parent_id?: string | null
  description?: string | null
  external_id?: string | null
  is_special?: boolean
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  full_name: string
  challenge_phrase?: string
  terms_of_service_accepted?: boolean
  recaptcha_token?: string
}

export interface UpdateUserRequest {
  username?: string
  email?: string
  full_name?: string
  password?: string
  is_active?: boolean
  role?: UserRole
}

// Navigation types
export interface NavigationItem {
  name: string
  href: string
  icon?: unknown
  current?: boolean
  requiresAuth?: boolean
  requiredRole?: UserRole
  children?: NavigationItem[]
}

// Audit Log types
export interface AuditLog {
  id: string
  event_type: string
  user_id: string | null
  actor_id: string | null
  event_data: Record<string, unknown>
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

// Theme types
export type Theme = 'css-light' | 'css-dark' | 'light' | 'dark' | 'cupcake' | 'corporate'

// Notification types
export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
}

// ===== Webhooks =====

/** A reusable, write-only auth credential. The secret value is never returned. */
export interface WebhookAuthHeader {
  id: string
  name: string
  header_name: string
  has_value: boolean
  created_at: string
  updated_at: string
}

export interface CreateAuthHeaderRequest {
  name: string
  header_name: string
  header_value: string
}

export interface UpdateAuthHeaderRequest {
  name?: string
  header_name?: string
  header_value?: string
}

/** A webhook subscription by class (#87): a severity floor, optionally within one category. */
export interface WebhookClassSubscription {
  category: string | null
  min_severity: string
}

export interface Webhook {
  id: string
  name: string
  url: string
  enabled: boolean
  signing_secret: string
  event_types: string[]
  class_subscriptions: WebhookClassSubscription[]
  auth_header_ids: string[]
  /** Delivery envelope: the signed audit JSON, or a Discord message. */
  format: 'json' | 'discord'
  created_at: string
  updated_at: string
}

export interface CreateWebhookRequest {
  name: string
  url: string
  enabled?: boolean
  event_types: string[]
  auth_header_ids: string[]
}

export interface UpdateWebhookRequest {
  name?: string
  url?: string
  enabled?: boolean
  event_types?: string[]
  auth_header_ids?: string[]
}

export interface WebhookEventType {
  value: string
  label: string
}

export interface WebhookDelivery {
  id: string
  webhook_id: string
  audit_log_id: string | null
  event_type: string
  attempt: number
  success: boolean
  status_code: number | null
  response_body: string | null
  error: string | null
  request_payload: unknown
  created_at: string
}

// --- RBAC administration (#65) ---
export interface RbacRole {
  id: string
  name: string
  description: string
  is_system: boolean
  level: number
  /** Role ids this role inherits from directly. */
  inherits: string[]
  /** Permission keys granted directly to this role (not via inheritance). */
  permissions: string[]
}

export interface RbacPermission {
  key: string
  description: string
}

export interface RbacConfig {
  roles: RbacRole[]
  permissions: RbacPermission[]
}

/** A role a user currently holds, as returned by GET /admin/users/{id}/roles. */
export interface AssignedUserRole {
  id: string
  name: string
}

export interface CreateRoleRequest {
  name: string
  description?: string
  level?: number
}

export interface UpdateRoleRequest {
  description?: string
  level?: number
}

// ── Tool module bindings + safety interlocks (#83) ───────────────────────────

/** A device bound to a resource in one role (#101; was `ToolModule`). */
export interface DeviceBinding {
  id: string
  /** #101: the resource (a tool, and from slice 4b a door) this binds to. */
  resource_id: string
  device_id: string
  /** `reader` | `power` | `sensor` | `edge` | `strike` */
  role: string
  name: string
  params: Record<string, unknown>
  /** `fail_off` | `hold_last` | `ignore` */
  on_disconnect: string
  created_at: string
  updated_at: string
  /** #179, `power` only: lower stages must confirm they energized first. */
  start_stage: number
  /** Spin-up after the stage before it confirms, in ms. */
  start_delay_ms: number
  /** Run-on after any denial, in ms. */
  run_on_ms: number
  /** #182, `strike` only: how long the latch is released on a yes, in ms. */
  pulse_ms: number
  /** #182, `strike` only: held released for an open-access window, or pulsed only. */
  hold_open_access: boolean
}

export interface CreateDeviceBindingRequest {
  resource_id: string
  device_id: string
  role: string
  name: string
  params?: Record<string, unknown>
  on_disconnect?: string
  start_stage?: number
  start_delay_ms?: number
  run_on_ms?: number
  /** #182, required on a `strike` binding, refused on any other. */
  pulse_ms?: number
  /** #182, `strike` only. */
  hold_open_access?: boolean
}

/** A configured interlock: an AND-ed start precondition or an OR-ed trip. */
export interface ToolInterlock {
  id: string
  tool_id: string
  /** `start_gate` | `trip` */
  kind: string
  condition: string
  source_module_id: string | null
  debounce_ms: number
  latch: boolean
  /** `re_auth` | `operator_ack` | `auto` */
  reset: string
  /** `firmware` | `edge` | `server` */
  enforcement: string
  enabled: boolean
  created_at: string
  updated_at: string
}

export interface CreateToolInterlockRequest {
  tool_id: string
  kind: string
  condition: string
  source_module_id?: string | null
  debounce_ms?: number
  latch?: boolean
  reset?: string
  enforcement?: string
  enabled?: boolean
}

/**
 * One resource's wiring as the edge receives it. #182: a door with bindings
 * appears here too, named by `kind`; `tool_id` is the resource's UUID whatever
 * its kind (the field name is the wire's history).
 */
export interface ToolModuleStateTool {
  tool_id: string
  external_id: string | null
  /** `tool` | `door` */
  kind: string
  /** `momentary` | `until_stopped` (#182); `''` from a server that predates it. */
  session_profile: string
  /** A session is open on this resource as the server knows it (#190). */
  in_use?: boolean
  modules: Array<{
    id: string
    device_id: string
    role: string
    name: string
    params: Record<string, unknown>
    on_disconnect: string
    start_stage: number
    start_delay_ms: number
    run_on_ms: number
    pulse_ms: number
    hold_open_access: boolean
  }>
  interlocks: Array<{
    id: string
    kind: string
    condition: string
    source_module_id: string | null
    debounce_ms: number
    latch: boolean
    reset: string
    enforcement: string
  }>
  /** Whether the modules that switch this tool can reach a safe state unaided. */
  power_fails_safe: boolean
}

export interface ToolModuleStateSnapshot {
  as_of: string
  tools: ToolModuleStateTool[]
}

/** #190: a resource in use, whatever its kind, and who opened it. */
export interface OpenSessionView {
  resource_id: string
  kind: string
  user_id: string | null
  username: string | null
  full_name: string | null
  opened_at: string
  device_id: string | null
}

/** #190: what ending a session from the platform reports back. */
export interface SessionEnded {
  resource_id: string
  kind: string
  ended: boolean
}

/** #182: what an administrator may set on a resource whatever its kind. */
export interface UpdateResourceRequest {
  session_profile?: 'momentary' | 'until_stopped'
}

export interface ResourceSummary {
  id: string
  kind: string
  session_profile: string
}
